// Downloads store to manage global download state
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Safari detection - more reliable than user-agent checks
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
};

// Helper to get file name from URL or path and prevent duplicate extensions
const getFileName = (url, suggestedFileName) => {
  if (suggestedFileName) {
    // Return the suggested name, preventing duplicate extensions
    return suggestedFileName;
  }
  
  if (!url) return 'unknown';
  let filename = url.split('/').pop().split('#')[0].split('?')[0] || 'unknown';
  
  // Remove any URL encoding
  try {
    filename = decodeURIComponent(filename);
  } catch (e) {
    // If decoding fails, use the original filename
  }
  
  return filename;
};

// Helper to prevent duplicate file extensions
const preventDuplicateExtension = (fileName, mimeType) => {
  if (!fileName) return 'unknown';
  
  // Get the extension from the filename
  const fileExtension = fileName.split('.').pop().toLowerCase();
  const baseFileName = fileName.includes('.') ? 
    fileName.substring(0, fileName.lastIndexOf('.')) : 
    fileName;
  
  // Common extensions map
  const mimeToExtMap = {
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/mpeg': 'mpeg',
    'audio/mpeg': 'mp3',
    'audio/mp4': 'm4a',
    'audio/wav': 'wav',
    'application/pdf': 'pdf',
    'application/zip': 'zip'
  };
  
  // Get the correct extension based on mime type
  let correctExtension = mimeToExtMap[mimeType];
  
  // If mime type doesn't provide an extension or the extension already matches, return original name
  if (!correctExtension || fileExtension === correctExtension) {
    return fileName;
  }
  
  // Add the correct extension
  return `${baseFileName}.${correctExtension}`;
};

// Handle page unload warnings when downloads are in progress
let hasActiveDownloads = false;

// Keep track of active blob URLs to revoke them properly
const activeBlobUrls = new Map();
const activeDownloadLinks = new Map();

// Only add beforeunload listener once
let beforeUnloadListenerAdded = false;

const setupBeforeUnloadListener = () => {
  if (beforeUnloadListenerAdded) return;
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('unload', cleanupAllBlobUrls);
  beforeUnloadListenerAdded = true;
};

const cleanupBeforeUnloadListener = () => {
  if (!beforeUnloadListenerAdded) return;
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener('unload', cleanupAllBlobUrls);
  beforeUnloadListenerAdded = false;
};

const cleanupAllBlobUrls = () => {
  for (const id of Array.from(activeBlobUrls.keys())) {
    revokeBlobUrl(id);
  }
  
  for (const link of Array.from(activeDownloadLinks.values())) {
    if (document.body.contains(link)) {
      document.body.removeChild(link);
    }
  }
  activeDownloadLinks.clear();
};

const handleBeforeUnload = (event) => {
  if (hasActiveDownloads) {
    // Standard way to show a confirmation dialog before leaving
    const message = 'You have active downloads. If you leave now, your downloads will be cancelled. Are you sure?';
    event.preventDefault();
    event.returnValue = message; // Legacy required for Chrome
    return message; // For other browsers
  }
};

// Revoke a blob URL safely
const revokeBlobUrl = (id) => {
  if (activeBlobUrls.has(id)) {
    try {
      window.URL.revokeObjectURL(activeBlobUrls.get(id));
      activeBlobUrls.delete(id);
    } catch (e) {
      console.error('Error revoking blob URL:', e);
    }
  }
  
  // Also clean up any download links
  if (activeDownloadLinks.has(id)) {
    try {
      const link = activeDownloadLinks.get(id);
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      activeDownloadLinks.delete(id);
    } catch (e) {
      console.error('Error removing download link:', e);
    }
  }
};

// Safari-specific download handler using ArrayBuffer
const safariDownload = (arrayBuffer, contentType, fileName) => {
  return new Promise((resolve) => {
    // For Safari, we'll use ArrayBuffer directly
    
    // Convert ArrayBuffer to a Blob with explicit type
    const blob = new Blob([arrayBuffer], { type: contentType });
    
    // Create a temporary link to download the file
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    
    // Set up the download
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    
    // Add to DOM and trigger click
    document.body.appendChild(link);
    
    // Click the link
    link.click();
    
    // Don't remove the link or revoke the URL immediately
    // Safari needs time to process the download
    setTimeout(() => {
      // Now clean up
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
      resolve();
    }, 10000); // Give Safari plenty of time (10 seconds)
  });
};

// Setup listener when module is loaded
setupBeforeUnloadListener();

export default {
  namespaced: true,
  state: {
    downloads: [], // Array of download objects with progress info
  },
  mutations: {
    ADD_DOWNLOAD(state, download) {
      state.downloads.push(download);
    },
    UPDATE_DOWNLOAD_PROGRESS(state, { id, progress, loaded, total }) {
      const download = state.downloads.find(d => d.id === id);
      if (download) {
        download.progress = progress;
        download.loaded = loaded;
        download.total = total;
      }
    },
    UPDATE_DOWNLOAD_STATUS(state, { id, status }) {
      const download = state.downloads.find(d => d.id === id);
      if (download) {
        download.status = status;
      }
    },
    REMOVE_DOWNLOAD(state, id) {
      const index = state.downloads.findIndex(d => d.id === id);
      if (index !== -1) {
        state.downloads.splice(index, 1);
      }
      
      // Also revoke any blob URL that might be associated with this download
      revokeBlobUrl(id);
    }
  },
  actions: {
    async downloadFile({ commit, state }, { url, fileName }) {
      // Create a download ID
      const id = uuidv4();
      
      // Get file name if not provided, preventing duplicate extensions
      const name = fileName || getFileName(url);
      
      // Check if we're using Safari
      const useSafari = isSafari();
      
      // Add the download to the state
      commit('ADD_DOWNLOAD', {
        id,
        url,
        fileName: name,
        progress: 0,
        loaded: 0,
        total: 0,
        status: 'pending', // pending, downloading, paused, completed, error
        cancelTokenSource: axios.CancelToken.source()
      });

      try {
        // Start the download
        commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'downloading' });
        
        // Set flag for active downloads
        hasActiveDownloads = true;
        
        // Use ArrayBuffer for Safari, Blob for other browsers
        const response = await axios({
          url,
          method: 'GET',
          responseType: useSafari ? 'arraybuffer' : 'blob',
          cancelToken: state.downloads.find(d => d.id === id).cancelTokenSource.token,
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            commit('UPDATE_DOWNLOAD_PROGRESS', {
              id,
              progress: percentCompleted,
              loaded: progressEvent.loaded,
              total: progressEvent.total
            });
          }
        });

        // Create blob and URL safely
        try {
          const contentType = response.headers['content-type'] || 'application/octet-stream';
          
          // Ensure filename has correct extension based on content-type
          const finalFileName = preventDuplicateExtension(name, contentType);
          
          // Check if we're using Safari
          if (useSafari) {
            // Use the ArrayBuffer directly for Safari
            await safariDownload(response.data, contentType, finalFileName);
          } else {
            // For other browsers, response.data is already a Blob
            const blob = response.data;
            const downloadUrl = window.URL.createObjectURL(blob);
            
            // Store the blob URL so we can revoke it later
            activeBlobUrls.set(id, downloadUrl);

            // Create download link
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = finalFileName;
            link.style.display = 'none';
            
            // Add to DOM safely
            document.body.appendChild(link);
            activeDownloadLinks.set(id, link);
            
            // Click to trigger download
            link.click();
            
            // Clean up after triggering download
            requestAnimationFrame(() => {
              // For other browsers we can remove the link quickly
              if (document.body.contains(link)) {
                document.body.removeChild(link);
              }
              activeDownloadLinks.delete(id);
            });
          }
          
          // Mark as completed
          commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'completed' });
          
          // Remove from UI after a delay so user can see it completed
          setTimeout(() => {
            commit('REMOVE_DOWNLOAD', id);
            
            // Update hasActiveDownloads flag based on remaining downloads
            hasActiveDownloads = state.downloads.some(d => d.status === 'downloading');
          }, 3000);
          
          return { success: true, id };
        } catch (e) {
          console.error('Error creating download:', e);
          commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'error' });
          // No auto-removal for error state - user must manually clear or retry
          
          throw e;
        }
      } catch (error) {
        console.error('Download failed:', error);
        if (axios.isCancel(error)) {
          commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'cancelled' });
          
          // Only auto-remove cancelled downloads, not failed ones
          setTimeout(() => {
            commit('REMOVE_DOWNLOAD', id);
            
            // Update hasActiveDownloads flag based on remaining downloads
            hasActiveDownloads = state.downloads.some(d => d.status === 'downloading');
          }, 2000);
        } else {
          commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'error' });
          // No auto-removal for error state - user must manually clear or retry
        }
        
        return { success: false, error, id };
      }
    },
    
    cancelDownload({ commit, state }, id) {
      const download = state.downloads.find(d => d.id === id);
      if (download && download.status === 'downloading') {
        download.cancelTokenSource.cancel('Download cancelled by user');
        commit('UPDATE_DOWNLOAD_STATUS', { id, status: 'cancelled' });
        
        setTimeout(() => {
          commit('REMOVE_DOWNLOAD', id);
          
          // Update hasActiveDownloads flag based on remaining downloads
          hasActiveDownloads = state.downloads.some(d => d.status === 'downloading');
        }, 2000);
      }
    },
    
    clearDownload({ commit, state }, id) {
      commit('REMOVE_DOWNLOAD', id);
      
      // Update hasActiveDownloads flag based on remaining downloads
      hasActiveDownloads = state.downloads.some(d => d.status === 'downloading');
    }
  },
  getters: {
    activeDownloads(state) {
      return state.downloads.filter(d => d.status === 'downloading' || d.status === 'pending');
    },
    allDownloads(state) {
      return state.downloads;
    },
    hasActiveDownloads() {
      return hasActiveDownloads;
    }
  }
};